import { convertTracingSheet } from "./tracing-sheet";
import { paperOpacity } from "../shared/trace-paper";
import type { TLShape, TLShapeId } from "@tldraw/tlschema";
import { useRef } from "react";
import type { WorkspaceModel } from "./useWorkspace";
import { containingTrace, duplicateTrace } from "./tracing";
import { imageImports } from "./image-imports";
import { notify } from "./api";

export function OpacityControl({
  model,
  shape,
  label,
}: {
  model: WorkspaceModel;
  shape: TLShape;
  label: string;
}) {
  const changing = useRef(false);
  const opacity = shape.type === "trace" ? paperOpacity(shape) : shape.opacity;
  const update = (value: number) => {
    if (!Number.isFinite(value)) return;
    if (!changing.current) {
      model.editor.markHistoryStoppingPoint(label, { hold: true });
      changing.current = true;
    }
    model.editor.updateShape({
      id: shape.id,
      type: shape.type,
      ...(shape.type === "trace"
        ? {
            opacity: 1,
            meta: {
              ...shape.meta,
              paperOpacity: Math.max(0, Math.min(100, value)) / 100,
            },
          }
        : { opacity: Math.max(0, Math.min(100, value)) / 100 }),
    });
  };
  return (
    <label>
      {label}
      <div className="trace-opacity">
        <input
          aria-label={label}
          type="range"
          min={0}
          max={100}
          step={1}
          value={Math.round(opacity * 100)}
          onChange={(e) => update(Number(e.target.value))}
          onPointerUp={() => {
            changing.current = false;
            model.editor.commitHistoryStoppingPoint();
          }}
          onBlur={() => {
            changing.current = false;
            model.editor.commitHistoryStoppingPoint();
          }}
        />
        <input
          aria-label={`${label} percent`}
          type="number"
          min={0}
          max={100}
          value={Math.round(opacity * 100)}
          onChange={(e) => update(e.target.valueAsNumber)}
          onBlur={() => {
            changing.current = false;
            model.editor.commitHistoryStoppingPoint();
          }}
        />
        <span>%</span>
      </div>
    </label>
  );
}
export function TraceControls({ model }: { model: WorkspaceModel }) {
  const fileInput = useRef<HTMLInputElement>(null);
  const { editor, selected } = model,
    shape = selected.length === 1 ? selected[0] : undefined;
  const trace = containingTrace(editor, shape);
  if (!shape || !trace) return null;
  if (shape.id !== trace.id)
    return (
      <button onClick={() => editor.select(trace.id)}>
        Tracing paper · {trace.props.name}
      </button>
    );
  return (
    <>
      <label>
        Trace name
        <input
          aria-label="Trace name"
          maxLength={100}
          value={trace.props.name}
          onChange={(e) =>
            editor.updateShape({
              id: trace.id,
              type: "trace",
              props: { name: e.target.value },
            })
          }
        />
      </label>
      <button
        onClick={() => {
          model.annotation.end();
          convertTracingSheet(editor, trace.id);
        }}
      >
        Convert to normal sheet
      </button>
      <button onClick={() => fileInput.current?.click()}>Add image</button>
      <input
        ref={fileInput}
        aria-label="Add image to trace"
        type="file"
        accept="image/png,image/jpeg,image/webp"
        multiple
        hidden
        onChange={(e) => {
          editor.select(trace.id);
          imageImports.get(editor)?.add(Array.from(e.target.files ?? []));
          e.target.value = "";
        }}
      />
      <button
        onClick={() => {
          model.annotation.end();
          duplicateTrace(editor, trace);
          const copy = editor.getOnlySelectedShape();
          if (copy?.type === "trace") model.annotation.begin(copy.id);
          notify("Original hidden — find it in Sheets.");
        }}
      >
        Try another idea
      </button>
      <button onClick={() => {
        model.annotation.end();
        editor.markHistoryStoppingPoint("Duplicate tracing sheet");
        editor.duplicateShapes([trace.id], { x: 32, y: 32 });
        const copy = editor.getOnlySelectedShape();
        if (copy?.type === "trace" && copy.id !== trace.id)
          editor.updateShape({ id: copy.id, type: "trace", props: { name: `${trace.props.name} — copy`, hidden: false } });
      }}>Duplicate sheet</button>
      {!trace.parentId.startsWith("page:") && editor.getShape(trace.parentId as TLShapeId) && (
        <button onClick={() => {
          const source = editor.getShape(trace.parentId as TLShapeId);
          if (!source) return;
          model.annotation.end();
          editor.select(source.id);
          const bounds = editor.getShapePageBounds(source);
          if (bounds) editor.zoomToBounds(bounds, { inset: 160, animation: { duration: 200 } });
        }}>Go to reference</button>
      )}
      <button
        onClick={() => {
          editor.markHistoryStoppingPoint("Hide trace");
          editor.updateShape({
            id: trace.id,
            type: "trace",
            props: { hidden: true },
          });
          editor.selectNone();
        }}
      >
        Hide sheet
      </button>
    </>
  );
}
